const FONT_SANS = '"Segoe UI", "Microsoft YaHei", "PingFang SC", sans-serif';
const FONT_MONO = '"Cascadia Code", "SFMono-Regular", Consolas, monospace';

export const COLORS = {
  text: '#e9f4ff',
  muted: 'rgba(178, 202, 222, 0.62)',
  faint: 'rgba(160, 190, 214, 0.28)',
  grid: 'rgba(142, 184, 218, 0.12)',
  white: '#ffffff',
  cyan: '#67e8f9',
  mint: '#5eead4',
  amber: '#fbbf24',
  pink: '#f472b6',
  red: '#fb7185',
  sky: '#7dd3fc',
  slate: '#0b1724',
};

export function hexToRgba(hex, alpha = 1) {
  const clean = hex.replace('#', '');
  const value = clean.length === 3
    ? clean.split('').map((char) => char + char).join('')
    : clean;
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function drawBackdrop(ctx, width, height, { grid = true, accent = COLORS.mint } = {}) {
  ctx.save();
  ctx.clearRect(0, 0, width, height);

  const glow = ctx.createRadialGradient(width * 0.52, height * 0.42, 10, width * 0.52, height * 0.42, Math.max(width, height) * 0.7);
  glow.addColorStop(0, hexToRgba(accent, 0.055));
  glow.addColorStop(0.55, 'rgba(10, 24, 38, 0.02)');
  glow.addColorStop(1, 'rgba(5, 12, 20, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, width, height);

  if (grid) {
    const step = 34;
    ctx.fillStyle = COLORS.grid;
    for (let x = step; x < width; x += step) {
      for (let y = step; y < height; y += step) {
        ctx.beginPath();
        ctx.arc(x, y, 0.85, 0, Math.PI * 2);
        ctx.fill();
      }
    }
    ctx.strokeStyle = 'rgba(123, 168, 202, 0.05)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(width * 0.5, 0);
    ctx.lineTo(width * 0.5, height);
    ctx.moveTo(0, height * 0.5);
    ctx.lineTo(width, height * 0.5);
    ctx.stroke();
  }

  const vignette = ctx.createRadialGradient(width / 2, height / 2, Math.min(width, height) * 0.3, width / 2, height / 2, Math.max(width, height) * 0.75);
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(2, 8, 14, 0.48)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, width, height);
  ctx.restore();
}

export function roundedRect(ctx, x, y, width, height, radius = 8) {
  const r = Math.min(radius, Math.abs(width) / 2, Math.abs(height) / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

export function drawArrow(ctx, from, to, options = {}) {
  const {
    color = COLORS.mint,
    width = 2,
    head = 8,
    dashed = false,
    alpha = 1,
    label = '',
    labelOffset = { x: 0, y: -10 },
    glow = false,
  } = options;
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy);
  if (length < 2) return;
  const ux = dx / length;
  const uy = dy / length;
  const endX = to.x - ux * head * 0.8;
  const endY = to.y - uy * head * 0.8;

  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineWidth = width;
  ctx.lineCap = 'round';
  ctx.setLineDash(dashed ? [5, 5] : []);
  if (glow) {
    ctx.shadowColor = hexToRgba(color, 0.8);
    ctx.shadowBlur = 12;
  }
  ctx.beginPath();
  ctx.moveTo(from.x, from.y);
  ctx.lineTo(endX, endY);
  ctx.stroke();
  ctx.setLineDash([]);
  ctx.beginPath();
  ctx.moveTo(to.x, to.y);
  ctx.lineTo(to.x - ux * head - uy * head * 0.48, to.y - uy * head + ux * head * 0.48);
  ctx.lineTo(to.x - ux * head + uy * head * 0.48, to.y - uy * head - ux * head * 0.48);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  if (label) {
    drawPill(ctx, label, to.x + labelOffset.x, to.y + labelOffset.y, {
      align: 'center',
      color,
      bg: 'rgba(7, 18, 29, 0.78)',
      border: hexToRgba(color, 0.35),
      font: `600 11px ${FONT_MONO}`,
      paddingX: 6,
      height: 20,
    });
  }
}

export function drawDashedLine(ctx, a, b, options = {}) {
  const { color = COLORS.faint, width = 1, dash = [4, 6], alpha = 1 } = options;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

export function drawPill(ctx, text, x, y, options = {}) {
  const {
    align = 'left',
    color = COLORS.text,
    bg = 'rgba(7, 17, 28, 0.72)',
    border = 'rgba(152, 192, 222, 0.16)',
    font = `500 11px ${FONT_SANS}`,
    paddingX = 8,
    height = 22,
    radius = 6,
  } = options;
  ctx.save();
  ctx.font = font;
  const width = ctx.measureText(text).width + paddingX * 2;
  let left = x;
  if (align === 'center') left = x - width / 2;
  if (align === 'right') left = x - width;
  roundedRect(ctx, left, y - height / 2, width, height, radius);
  ctx.fillStyle = bg;
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, left + paddingX, y + 0.5);
  ctx.restore();
  return { width, left };
}

export function drawText(ctx, text, x, y, options = {}) {
  const {
    color = COLORS.text,
    font = `500 12px ${FONT_SANS}`,
    align = 'left',
    baseline = 'middle',
    alpha = 1,
    shadow = false,
  } = options;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.font = font;
  ctx.fillStyle = color;
  ctx.textAlign = align;
  ctx.textBaseline = baseline;
  if (shadow) {
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = 4;
  }
  ctx.fillText(text, x, y);
  ctx.restore();
}

export function drawArc(ctx, center, radius, start, end, options = {}) {
  const { color = COLORS.faint, width = 1.4, dash = [], alpha = 1, anticlockwise = false } = options;
  ctx.save();
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.setLineDash(dash);
  ctx.beginPath();
  ctx.arc(center.x, center.y, radius, start, end, anticlockwise);
  ctx.stroke();
  ctx.restore();
}

export function drawBall(ctx, x, y, radius, color, options = {}) {
  const { glow = true, stroke = 'rgba(255,255,255,.55)' } = options;
  ctx.save();
  if (glow) {
    ctx.shadowColor = hexToRgba(color, 0.75);
    ctx.shadowBlur = radius * 1.6;
  }
  const gradient = ctx.createRadialGradient(
    x - radius * 0.35,
    y - radius * 0.4,
    radius * 0.1,
    x,
    y,
    radius,
  );
  gradient.addColorStop(0, '#ffffff');
  gradient.addColorStop(0.2, hexToRgba(color, 0.95));
  gradient.addColorStop(1, hexToRgba(color, 0.58));
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = gradient;
  ctx.fill();
  ctx.shadowBlur = 0;
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
}

export function drawTrail(ctx, points, color, options = {}) {
  if (!points || points.length < 2) return;
  const { width = 2, alpha = 0.45 } = options;
  ctx.save();
  ctx.lineCap = 'round';
  for (let i = 1; i < points.length; i += 1) {
    const progress = i / points.length;
    ctx.globalAlpha = alpha * progress * progress;
    ctx.strokeStyle = color;
    ctx.lineWidth = width * (0.35 + progress * 0.65);
    ctx.beginPath();
    ctx.moveTo(points[i - 1].x, points[i - 1].y);
    ctx.lineTo(points[i].x, points[i].y);
    ctx.stroke();
  }
  ctx.restore();
}

export function drawGridLabel(ctx, text, x, y, color = COLORS.muted) {
  drawText(ctx, text, x, y, { color, font: `500 10px ${FONT_MONO}` });
}

export function drawEnergyRing(ctx, x, y, radius, ratio, color) {
  const clamped = Math.min(1, Math.max(0, ratio));
  ctx.save();
  ctx.lineWidth = 4;
  ctx.strokeStyle = 'rgba(159, 190, 215, 0.12)';
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.stroke();
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.arc(x, y, radius, -Math.PI / 2, -Math.PI / 2 + clamped * Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

export function truncate(value, length = 18) {
  return value.length > length ? `${value.slice(0, length)}…` : value;
}
